/**
 * Outlook add-in (§4.3): the case, in the mailbox.
 *
 * This route has two faces, chosen at runtime by `useOfficeItem`:
 *
 *   - **Inside Outlook** (the real Office task pane): `OfficePane` reads the
 *     message the user has open (subject, plain-text body, sender) in the user's
 *     OWN Outlook session (ReadItem only, no Graph, no other mailbox), and runs
 *     the read-only `/api/quote/preview` engine to render the glass-box quote draft.
 *
 *     When that message is already in the case store (looked up by its
 *     `internetMessageId`), the pane shows the stored case and the sales reply
 *     instead of a fresh preview.
 *
 *   - **In a plain browser** (the `/addin` route): production explains how to open
 *     the pane from Outlook. Only the local dev server keeps the fixture demo, a
 *     thin task-pane beside a mock of the open Outlook mail.
 *
 * Shadow mode: nothing here ever sends, saves, replies, drafts or writes anything
 * back to the mailbox, to Go Autonomous or to F&O. There is no compose, reply or
 * send capability in the add-in.
 */

import { Icon, Inbox } from "@norican/icons";
import { Alert, Card, EmptyState, Spinner, Typography } from "@norican/react";

import { useCaseDetail } from "@/lib/data";
import { type QuoteCaseDetail } from "@/lib/mock";

import { CasePane } from "./case-pane";
import { OfficePane } from "./office-pane";
import { useOfficeItem } from "./use-office-item";

/** The fixture case for the standalone demo, local dev server only. */
const DEMO_CASE_ID = "RFQ-4821";
const DEMO_MAIL_SUBJECT = "Line down, urgent quote please";
const SHOW_DEV_DEMO = import.meta.env.DEV;

function OutlookMail({ detail }: { detail: QuoteCaseDetail }) {
  const mail = detail.thread[0];
  if (!mail) {
    return null;
  }
  return (
    <Card variant="outline" className="min-w-[300px] flex-1 overflow-hidden">
      <div className="bg-bg-inverse text-fg-on-inverse flex items-center gap-2 px-3.5 py-2.5 text-xs font-bold">
        <Icon icon={Inbox} size="sm" />
        Outlook: Aftermarket UK (shared mailbox)
      </div>
      <Alert tone="info" className="rounded-none border-x-0">
        Norican I2Q · processed: draft ready, needs review
      </Alert>
      <Card.Body className="bg-surface flex flex-col gap-1">
        <span className="text-fg font-bold">{DEMO_MAIL_SUBJECT}</span>
        <span className="text-fg-subtle text-xs">
          {mail.from} · {mail.when}
        </span>
        <p className="text-fg mt-1 text-[0.8125rem] leading-relaxed">{mail.body}</p>
      </Card.Body>
    </Card>
  );
}

/** Production, outside Outlook: say how to get the real pane. No sample data. */
function OpenFromOutlook() {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <Typography
          as="h1"
          variant="heading-4"
          className="font-display leading-display font-normal"
        >
          Outlook add-in
        </Typography>
        <Typography variant="body-sm" tone="muted" className="max-w-2xl">
          The add-in runs inside Outlook, next to the open mail. It shows the stored case for that
          mail and the reply of sales, or a read-only preview. Norican I2Q works on customer
          inquiries only. Shadow mode: nothing is sent.
        </Typography>
      </div>
      <Card variant="outline">
        <EmptyState
          className="py-12"
          icon={<Icon icon={Inbox} size="lg" className="text-fg-subtle" />}
          title="Open this from Outlook"
          description="No Outlook mail is open here. Open a mail in Outlook. Then start Norican I2Q from the ribbon to see its case."
        />
      </Card>
    </div>
  );
}

/** Local dev only: the fixture case beside a mock of the open Outlook mail. */
function StandaloneDemo() {
  const { data: detail } = useCaseDetail(DEMO_CASE_ID);

  if (!detail) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <Typography
            as="h1"
            variant="heading-4"
            className="font-display leading-display font-normal"
          >
            Outlook add-in
          </Typography>
          <Typography variant="body-sm" tone="muted">
            No case is open in the mailbox.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Typography
          as="h1"
          variant="heading-4"
          className="font-display leading-display font-normal"
        >
          Outlook add-in: the case, in the mailbox
        </Typography>
        <Typography variant="body-sm" tone="muted" className="max-w-2xl">
          Local dev sample. A thin task pane with no business logic: what Norican I2Q does with the
          open mail, the match summary, the reply of sales, and a link into the workbench. Open this
          route from inside Outlook to read your own open mail instead of this sample.
        </Typography>
      </div>
      <div className="flex flex-wrap items-start gap-6">
        <OutlookMail detail={detail} />
        <CasePane detail={detail} className="w-full shrink-0 sm:w-80" />
      </div>
    </div>
  );
}

/**
 * Route dispatcher: the real Office task pane when running inside Outlook; outside
 * Outlook the dev fixture demo (local dev server) or an "open from Outlook" state. The Office probe is read-only and resolves to
 * `standalone` for any non-Outlook load, so a plain-browser visit never crashes.
 */
export function AddinPage() {
  const office = useOfficeItem();

  if (office.isLoading) {
    return (
      <div className="flex items-center gap-3 p-2">
        <Spinner size="sm" label="Checking for an open Outlook mail…" />
        <Typography variant="body-sm" tone="muted">
          Checking for an open Outlook mail…
        </Typography>
      </div>
    );
  }

  if (office.data?.kind === "item") {
    return <OfficePane mail={office.data} />;
  }

  return SHOW_DEV_DEMO ? <StandaloneDemo /> : <OpenFromOutlook />;
}
